import { SENDER_IDENTITY_LABELS } from "@/components/SenderIdentitySelect";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@a7y/tailwind-catalyst";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import type { ProductEmailSend, ProductEmailStatus } from "./types";

const STATUS_COLORS: Record<ProductEmailStatus, string> = {
  draft: "zinc",
  sending: "blue",
  sent: "green",
  partial_failure: "amber",
  failed: "red",
};

export function SendHistoryTab() {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["product-email-sends"],
    queryFn: () => api.listSends({ limit: 50 }),
  });

  const sends: ProductEmailSend[] = data?.sends ?? [];

  return (
    <Table dense striped>
      <TableHead>
        <TableRow>
          <TableHeader>Subject</TableHeader>
          <TableHeader>Sender</TableHeader>
          <TableHeader>Recipients</TableHeader>
          <TableHeader>Status</TableHeader>
          <TableHeader>Sent At</TableHeader>
        </TableRow>
      </TableHead>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={5} className="text-center">
              <div className="mx-auto h-6 w-6 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600" />
            </TableCell>
          </TableRow>
        ) : sends.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} className="py-8 text-center">
              <p className="text-zinc-500">No emails sent yet</p>
            </TableCell>
          </TableRow>
        ) : (
          sends.map((send) => (
            <TableRow
              key={send.id}
              className="cursor-pointer"
              onClick={() =>
                navigate(`/admin/product-emails/sends/${send.id}`)
              }
            >
              <TableCell className="font-medium">{send.subject}</TableCell>
              <TableCell>
                {SENDER_IDENTITY_LABELS[send.senderIdentity] ?? send.senderIdentity}
              </TableCell>
              <TableCell>{send.recipientCount}</TableCell>
              <TableCell>
                <Badge
                  color={
                    STATUS_COLORS[send.status] as
                      | "zinc"
                      | "blue"
                      | "green"
                      | "amber"
                      | "red"
                  }
                >
                  {send.status.replace("_", " ")}
                </Badge>
              </TableCell>
              <TableCell className="text-zinc-500">
                {send.sentAt ? formatDate(send.sentAt) : formatDate(send.createdAt)}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
